import { motion } from "framer-motion";
import { Copy, Check, Mail, MapPin, Rocket, TrendingUp } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import Reveal from "./Reveal";

const REVENUE = [
  {
    n: "01",
    title: "Partnerships",
    body: "Revenue share and featured placement fees from production companies and venues leveraging Lantix's targeted audience reach.",
  },
  {
    n: "02",
    title: "Data & insights",
    body: "Actionable audience behavior and event performance insights for smarter promotions.",
  },
  {
    n: "03",
    title: "Social commerce",
    body: "Group purchases, referral rewards and shared planning built into the platform, so every member becomes a distribution channel.",
  },
];

const PHASES = [
  {
    icon: MapPin,
    phase: "Now: Foundation",
    points: ["Business model defined", "Market validation conducted", "UX roadmap finalized & core team assembled"],
  },
  {
    icon: Rocket,
    phase: "0–3 months: Boston beta",
    points: ["Launch the Boston beta", "Onboard 10–25 venue & production partners", "1,000–5,000 active users"],
  },
  {
    icon: TrendingUp,
    phase: "6–12 months: Growth",
    points: ["Ambassador programs & paid channels", "Go-to platform for Boston's live scene", "Validate the core discovery experience"],
  },
];

const FUNDS = [
  { label: "Product development", pct: 35 },
  { label: "Marketing & user acquisition", pct: 25 },
  { label: "Business development & partnerships", pct: 20 },
  { label: "Operations & legal", pct: 10 },
  { label: "Contingency", pct: 10 },
];

const INVEST_EMAIL = "invest@lantixapp.com";

export default function Investor() {
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(INVEST_EMAIL);
      setCopied(true);
      toast.success("Email copied", { description: INVEST_EMAIL });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy. Email us at " + INVEST_EMAIL);
    }
  };

  return (
    <section id="investors" className="relative overflow-hidden py-24 lg:py-32">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-[#150736] to-[#2b0086]/40" aria-hidden="true" />
      <div
        className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-brand-purple opacity-15 blur-[180px]"
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <span className="w-max rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-brand-gold">
            Investment readiness · $4M seed raise
          </span>
          <h2 className="mt-5 max-w-3xl font-display text-3xl font-extrabold uppercase leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            The market is ripe for <span className="text-gradient">disruption.</span>
          </h2>
          <p className="mt-4 max-w-2xl text-brand-mist/65">
            Business model, market research and product UX roadmap are validated and documented.
            The next phase: MVP development and initial traction in Boston.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-4 lg:grid-cols-4" data-testid="investor-stats">
          {[
            { stat: "70%+", label: "of consumers report frustration with legacy platforms: hidden fees, poor UX and no local discovery" },
            { stat: "$4M", label: "Seed raise to fund product development, market entry and growth across Boston" },
            { stat: "10–25", label: "venue & production partners targeted across the Boston market" },
            { stat: "12 mo", label: "to MVP launch, the Boston beta and initial traction milestones" },
          ].map((s, i) => (
            <Reveal key={s.stat} delay={i * 0.08}>
              <div className="h-full rounded-3xl border border-brand-purple/20 bg-brand-card/80 p-7 backdrop-blur-xl transition-colors duration-500 hover:border-brand-gold/50">
                <p className="font-display text-4xl font-extrabold text-brand-gold lg:text-5xl" data-testid={`investor-stat-${i + 1}`}>
                  {s.stat}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-brand-mist/65">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 grid gap-14 lg:grid-cols-2">
          <div>
            <Reveal>
              <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-brand-paper">
                Three revenue streams
              </h3>
              <p className="mt-2 text-sm text-brand-mist/60">
                Monetizing every touchpoint, from discovery to purchase.
              </p>
            </Reveal>
            <div className="mt-8 space-y-4" data-testid="revenue-streams">
              {REVENUE.map((r, i) => (
                <Reveal key={r.n} delay={i * 0.07}>
                  <div className="group flex gap-5 rounded-2xl border border-brand-purple/20 bg-brand-card/70 p-5 backdrop-blur-xl transition-all duration-500 hover:border-brand-purple/55 hover:bg-brand-card">
                    <span className="font-brand text-sm font-bold text-brand-gold/80">{r.n}</span>
                    <div>
                      <p className="font-display text-lg font-bold text-brand-paper">{r.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-brand-mist/65">{r.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div>
            <Reveal>
              <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-brand-paper">
                Use of funds
              </h3>
              <p className="mt-2 text-sm text-brand-mist/60">
                Capital deployed across five strategic areas within 12 months.
              </p>
            </Reveal>
            <div className="mt-8 space-y-5" data-testid="use-of-funds">
              {FUNDS.map((f, i) => (
                <Reveal key={f.label} delay={i * 0.05}>
                  <div>
                    <div className="mb-2 flex items-center justify-between text-sm">
                      <span className="font-medium text-brand-paper/90">{f.label}</span>
                      <span className="font-bold text-brand-teal">{f.pct}%</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-white/10">
                      <motion.div
                        className="h-full rounded-full bg-gradient-to-r from-brand-purple to-brand-teal"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${f.pct}%` }}
                        viewport={{ once: true, amount: 0.6 }}
                        transition={{ duration: 1.1, delay: 0.15 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                      />
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.2}>
              <div className="mt-10 rounded-3xl border border-brand-gold/30 bg-brand-gold/5 p-7">
                <h3 className="font-display text-xl font-bold uppercase tracking-tight text-brand-paper">
                  Invest with us
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-mist/70">
                  Investment at this stage directly accelerates product launch and partner
                  acquisition. Reach the team behind Lantix at True Life Entertainment, Inc.
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={copyEmail}
                    data-testid="invest-email-copy-button"
                    className="flex items-center gap-2 rounded-full border border-brand-gold/50 bg-brand-gold/10 px-5 py-3 text-sm font-bold text-brand-gold transition-all duration-300 hover:bg-brand-gold hover:text-black"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? "Copied" : INVEST_EMAIL}
                  </button>
                  <a
                    href={`mailto:${INVEST_EMAIL}`}
                    data-testid="invest-email-link"
                    className="flex items-center gap-2 rounded-full bg-brand-teal px-5 py-3 text-sm font-bold text-black shadow-[0_0_28px_rgba(66,246,143,0.3)] transition-all duration-300 hover:shadow-[0_0_44px_rgba(66,246,143,0.55)]"
                  >
                    <Mail className="h-4 w-4" /> Email the team
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-20" data-testid="roadmap">
          <Reveal>
            <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-brand-paper">
              Growth &amp; expansion
            </h3>
          </Reveal>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {PHASES.map((p, i) => (
              <Reveal key={p.phase} delay={i * 0.1}>
                <div className="group relative h-full overflow-hidden rounded-3xl border border-brand-purple/20 bg-brand-card/80 p-7 backdrop-blur-xl transition-all duration-500 hover:border-brand-teal/50">
                  <span className="font-brand text-5xl font-extrabold text-white/[0.06] transition-colors duration-500 group-hover:text-brand-purple/15">
                    0{i + 1}
                  </span>
                  <div className="-mt-8 flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-brand-teal/30 bg-brand-teal/10 text-brand-teal">
                      <p.icon className="h-4 w-4" />
                    </span>
                    <p className="font-display text-lg font-bold text-brand-paper">{p.phase}</p>
                  </div>
                  <ul className="mt-5 space-y-2.5">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-2.5 text-sm text-brand-mist/70">
                        <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-brand-gold" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
